import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  FaMapMarkerAlt,
  FaSpinner,
} from 'react-icons/fa';

import { useProfileMutation } from '../slices/usersApiSlice';
import { setCredentials } from '../slices/authSlice';
import Loader from '../components/Loader';

// Replace this with your actual Google Maps API key.
const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_API_KEY;

const MyProfile = ({ onProfileUpdate }) => {
  const [name, setName] = useState('');
  const [phoneNo, setPhoneNo] = useState('');

  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');

  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordFields, setShowPasswordFields] = useState(false);

  const { userInfo } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const [updateProfile, { isLoading: loadingUpdateProfile }] =
    useProfileMutation();

  useEffect(() => {
    if (!userInfo) {
      return;
    }

    setName(userInfo.name || '');
    setPhoneNo(userInfo.phoneNo || '');

    setDeliveryAddress(
      userInfo.deliveryAddress?.street || ''
    );

    setCity(
      userInfo.deliveryAddress?.city || ''
    );

    setPostalCode(
      userInfo.deliveryAddress?.postalCode || ''
    );

    setLatitude(
      userInfo.location?.latitude ??
        userInfo.latitude ??
        null
    );

    setLongitude(
      userInfo.location?.longitude ??
        userInfo.longitude ??
        null
    );
  }, [userInfo]);

  const getAddressComponent = (
    addressComponents,
    componentTypes
  ) => {
    const component = addressComponents.find((item) =>
      componentTypes.some((type) =>
        item.types.includes(type)
      )
    );

    return component?.long_name || '';
  };

  const cleanStreetAddress = (
    formattedAddress,
    detectedCity,
    detectedPostalCode
  ) => {
    let cleanedAddress = formattedAddress || '';

    const removableValues = [
      detectedCity,
      detectedPostalCode,
      'Andhra Pradesh',
      'India',
    ].filter(Boolean);

    removableValues.forEach((value) => {
      cleanedAddress = cleanedAddress.replace(value, '');
    });

    return cleanedAddress
      .replace(/,\s*,/g, ',')
      .replace(/^,\s*|\s*,$/g, '')
      .trim();
  };

  const fetchAddressFromGoogle = async (
    currentLatitude,
    currentLongitude
  ) => {
    try {
      if (
        !GOOGLE_MAPS_API_KEY ||
        GOOGLE_MAPS_API_KEY ===
          'PASTE_YOUR_GOOGLE_API_KEY_HERE'
      ) {
        toast.error(
          'Please add your Google Maps API key in MyProfile.jsx'
        );

        return false;
      }

      const requestUrl =
        'https://maps.googleapis.com/maps/api/geocode/json' +
        `?latlng=${currentLatitude},${currentLongitude}` +
        '&region=in' +
        `&key=${GOOGLE_MAPS_API_KEY}`;

      const response = await fetch(requestUrl);

      if (!response.ok) {
        throw new Error(
          `Google API request failed: ${response.status}`
        );
      }

      const data = await response.json();

      if (
        data.status !== 'OK' ||
        !Array.isArray(data.results) ||
        data.results.length === 0
      ) {
        console.error(
          'Google Geocoding response:',
          data
        );

        if (data.status === 'REQUEST_DENIED') {
          toast.error(
            data.error_message ||
              'Google request denied. Check API key, billing and Geocoding API.'
          );
        } else if (data.status === 'ZERO_RESULTS') {
          toast.error(
            'Google could not find an address for this location.'
          );
        } else if (data.status === 'OVER_QUERY_LIMIT') {
          toast.error(
            'Google Maps usage limit has been reached.'
          );
        } else {
          toast.error(
            data.error_message ||
              `Google Maps error: ${data.status}`
          );
        }

        return false;
      }

      const results = data.results;

      const preferredResult =
        results.find((result) =>
          result.types.includes('street_address')
        ) ||
        results.find((result) =>
          result.types.includes('premise')
        ) ||
        results.find((result) =>
          result.types.includes('subpremise')
        ) ||
        results.find((result) =>
          result.types.includes('route')
        ) ||
        results.find((result) =>
          result.types.includes('plus_code')
        ) ||
        results[0];

      const components =
        preferredResult.address_components || [];

      const streetNumber = getAddressComponent(
        components,
        ['street_number']
      );

      const premise = getAddressComponent(
        components,
        ['premise']
      );

      const subPremise = getAddressComponent(
        components,
        ['subpremise']
      );

      const route = getAddressComponent(
        components,
        ['route']
      );

      const area = getAddressComponent(
        components,
        [
          'neighborhood',
          'sublocality_level_3',
          'sublocality_level_2',
          'sublocality_level_1',
          'sublocality',
        ]
      );

      const locality = getAddressComponent(
        components,
        ['locality']
      );

      const administrativeArea3 =
        getAddressComponent(
          components,
          ['administrative_area_level_3']
        );

      const village = getAddressComponent(
        components,
        ['administrative_area_level_4']
      );

      const detectedPostalCode =
        getAddressComponent(
          components,
          ['postal_code']
        );

      const detectedCity =
        locality ||
        administrativeArea3 ||
        village ||
        area;

      const streetParts = [
        subPremise,
        premise,
        streetNumber,
        route,
        area,
      ].filter(Boolean);

      let finalStreetAddress =
        streetParts.join(', ');

      if (!finalStreetAddress) {
        finalStreetAddress =
          cleanStreetAddress(
            preferredResult.formatted_address,
            detectedCity,
            detectedPostalCode
          );
      }

      if (!finalStreetAddress) {
        toast.error(
          'Google could not identify the street address. Please enter it manually.'
        );

        return false;
      }

      const cleanedPostalCode =
        detectedPostalCode
          .replace(/\D/g, '')
          .slice(0, 6);

      setDeliveryAddress(finalStreetAddress);
      setCity(detectedCity || '');
      setPostalCode(cleanedPostalCode);

      return true;
    } catch (error) {
      console.error(
        'Google reverse geocoding error:',
        error
      );

      toast.error(
        'Unable to load the address from Google Maps.'
      );

      return false;
    }
  };

  const handleFetchLocationClick = () => {
    if (!navigator.geolocation) {
      toast.error(
        'Geolocation is not supported by this browser.'
      );

      return;
    }

    setIsGettingLocation(true);

    let bestPosition = null;
    let watchId = null;
    let completed = false;
    let locationTimer = null;

    const clearLocationTracking = () => {
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
      }

      if (locationTimer !== null) {
        clearTimeout(locationTimer);
      }
    };

    const finishLocationRequest = async () => {
      if (completed) {
        return;
      }

      completed = true;
      clearLocationTracking();

      if (!bestPosition) {
        setIsGettingLocation(false);

        toast.error(
          'Unable to get your current location.'
        );

        return;
      }

      const {
        latitude: currentLatitude,
        longitude: currentLongitude,
        accuracy,
      } = bestPosition.coords;

      console.log(
        'Selected location:',
        currentLatitude,
        currentLongitude,
        'Accuracy:',
        accuracy,
        'metres'
      );

      /*
       * A location with more than 5 km accuracy is too
       * unreliable for automatically changing an address.
       */
      if (accuracy > 5000) {
        setIsGettingLocation(false);

        toast.error(
          `Laptop location is too inaccurate. Accuracy is around ${Math.round(
            accuracy / 1000
          )} km. Please enter the address manually or use a GPS-enabled phone.`
        );

        return;
      }

      const addressUpdated =
        await fetchAddressFromGoogle(
          currentLatitude,
          currentLongitude
        );

      setIsGettingLocation(false);

      if (!addressUpdated) {
        return;
      }

      setLatitude(currentLatitude);
      setLongitude(currentLongitude);

      if (accuracy > 1000) {
        toast.warning(
          `Approximate location used. Accuracy is around ${Math.round(
            accuracy / 1000
          )} km. Please verify the address before updating.`
        );
      } else if (accuracy > 200) {
        toast.warning(
          `Approximate location used. Accuracy is around ${Math.round(
            accuracy
          )} metres. Please verify the address.`
        );
      } else {
        toast.success(
          'Street, city and PIN code updated'
        );
      }
    };

    watchId =
      navigator.geolocation.watchPosition(
        (position) => {
          if (completed) {
            return;
          }

          if (
            !bestPosition ||
            position.coords.accuracy <
              bestPosition.coords.accuracy
          ) {
            bestPosition = position;
          }

          /*
           * Stop early when the browser returns a
           * reasonably accurate position.
           */
          if (
            position.coords.accuracy <= 100
          ) {
            finishLocationRequest();
          }
        },
        (error) => {
          if (completed) {
            return;
          }

          completed = true;
          clearLocationTracking();
          setIsGettingLocation(false);

          console.error(
            'Location error:',
            error
          );

          switch (error.code) {
            case error.PERMISSION_DENIED:
              toast.error(
                'Location permission is blocked. Allow Location in browser settings.'
              );
              break;

            case error.POSITION_UNAVAILABLE:
              toast.error(
                'Current location is unavailable. Turn on Windows Location Services and Wi-Fi.'
              );
              break;

            case error.TIMEOUT:
              toast.error(
                'Location request timed out. Please try again.'
              );
              break;

            default:
              toast.error(
                'Unable to retrieve your current location.'
              );
          }
        },
        {
          enableHighAccuracy: true,
          maximumAge: 0,
          timeout: 30000,
        }
      );

    /*
     * Laptop location may never reach GPS-level accuracy.
     * After 12 seconds, use the best available position.
     */
    locationTimer = setTimeout(() => {
      finishLocationRequest();
    }, 12000);
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error(
        'Please enter your name'
      );

      return;
    }

    if (!deliveryAddress.trim()) {
      toast.error(
        'Please enter your street address'
      );

      return;
    }

    if (!city.trim()) {
      toast.error(
        'Please enter your city'
      );

      return;
    }

    if (!/^[0-9]{6}$/.test(postalCode)) {
      toast.error(
        'Please enter a valid 6-digit PIN code'
      );

      return;
    }

    if (showPasswordFields) {
      if (!password.trim()) {
        toast.error(
          'Please enter a new password'
        );

        return;
      }

      if (password !== confirmPassword) {
        toast.error(
          'Passwords do not match'
        );

        return;
      }
    }

    const payload = {
      name: name.trim(),
      phoneNo,

      deliveryAddress: {
        street: deliveryAddress.trim(),
        city: city.trim(),
        postalCode: postalCode.trim(),
      },

      location: {
        latitude:
          latitude !== null
            ? Number(latitude)
            : null,

        longitude:
          longitude !== null
            ? Number(longitude)
            : null,
      },
    };

    if (
      showPasswordFields &&
      password.trim()
    ) {
      payload.password = password;
    }

    try {
      const res =
        await updateProfile(payload).unwrap();

      dispatch(
        setCredentials({
          ...userInfo,
          ...res,
          token: res.token || userInfo.token,
        })
      );

      setPassword('');
      setConfirmPassword('');
      setShowPasswordFields(false);

      toast.success(
        'Profile updated successfully'
      );

      if (
        typeof onProfileUpdate ===
        'function'
      ) {
        onProfileUpdate();
      }
    } catch (err) {
      toast.error(
        err?.data?.message ||
          err?.error ||
          'Unable to update profile'
      );
    }
  };

  const handlePasswordResetClick = () => {
    setShowPasswordFields(
      (previousValue) =>
        !previousValue
    );

    setPassword('');
    setConfirmPassword('');
  };

  return (
    <div className="w-full min-w-0 bg-white">
      <h2 className="mb-3 text-lg font-semibold text-gray-900">
        User Profile
      </h2>

      <form
        onSubmit={submitHandler}
        className="space-y-3"
      >
        <div className="space-y-1">
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700"
          >
            Name
          </label>

          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) =>
              setName(e.target.value)
            }
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-100"
          />
        </div>

        <div className="space-y-1">
          <label
            htmlFor="tel"
            className="block text-sm font-medium text-gray-700"
          >
            Phone Number
          </label>

          <input
            type="tel"
            id="tel"
            value={phoneNo}
            disabled
            className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600"
          />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="address"
            className="block text-sm font-medium text-gray-700"
          >
            Street Address
          </label>

          <input
            type="text"
            id="address"
            placeholder="Enter your street address"
            value={deliveryAddress}
            onChange={(e) =>
              setDeliveryAddress(
                e.target.value
              )
            }
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-100"
          />

          <label
            htmlFor="city"
            className="block text-sm font-medium text-gray-700"
          >
            City
          </label>

          <input
            type="text"
            id="city"
            placeholder="Enter your city"
            value={city}
            onChange={(e) =>
              setCity(e.target.value)
            }
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-100"
          />

          <label
            htmlFor="postalCode"
            className="block text-sm font-medium text-gray-700"
          >
            PIN Code
          </label>

          <input
            type="text"
            id="postalCode"
            inputMode="numeric"
            maxLength={6}
            placeholder="Enter your PIN code"
            value={postalCode}
            onChange={(e) =>
              setPostalCode(
                e.target.value
                  .replace(/\D/g, '')
                  .slice(0, 6)
              )
            }
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-100"
          />

          <div className="pt-1">
            <button
              type="button"
              onClick={
                handleFetchLocationClick
              }
              disabled={
                isGettingLocation
              }
              className="inline-flex w-auto items-center justify-center gap-2 rounded-md bg-green-700 px-4 py-2 text-sm font-semibold text-white hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isGettingLocation ? (
                <FaSpinner className="animate-spin" />
              ) : (
                <FaMapMarkerAlt />
              )}

              {isGettingLocation
                ? 'Getting Location...'
                : 'Use Current Location'}
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button
            type="button"
            onClick={
              handlePasswordResetClick
            }
            className="inline-flex w-auto items-center justify-center rounded-md border border-green-700 bg-white px-4 py-2 text-sm font-semibold text-green-700 hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            {showPasswordFields
              ? 'Hide Password Fields'
              : 'Reset Password'}
          </button>
        </div>

        {showPasswordFields && (
          <>
            <div className="space-y-1">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                New Password
              </label>

              <input
                type="password"
                id="password"
                placeholder="Enter new password"
                value={password}
                onChange={(e) =>
                  setPassword(
                    e.target.value
                  )
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-100"
              />
            </div>

            <div className="space-y-1">
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700"
              >
                Confirm New Password
              </label>

              <input
                type="password"
                id="confirmPassword"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) =>
                  setConfirmPassword(
                    e.target.value
                  )
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-100"
              />
            </div>
          </>
        )}

        <div className="pt-1">
          <button
            type="submit"
            disabled={
              loadingUpdateProfile ||
              isGettingLocation
            }
            className="inline-flex w-auto min-w-[6rem] items-center justify-center rounded-md bg-green-700 px-5 py-2 text-sm font-semibold text-white hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loadingUpdateProfile
              ? 'Updating...'
              : 'Update'}
          </button>
        </div>

        {loadingUpdateProfile && (
          <Loader />
        )}
      </form>
    </div>
  );
};

export default MyProfile;